import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
	DataSetComponent,
	DropdownOption,
} from 'src/app/components/data-set/data-set.component';
import { CompanyEntityRelationship } from 'src/app/models/data/entityRelationship';
import { ApiLoadingKeys, QueryParam } from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetContentComponent,
} from '../data-set/data-set.component';
import { DataSetSearchComponent } from '../data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import {
	DataSetTableComponent,
	DataSetRowClickDirective,
} from '../data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { AddressPipe } from '../pipes';
import { DataSetLoadingRowComponent } from '../data-set/data-set-loading-row.component';

@Component({
	selector: 'app-company-entity-relationship-table',
	templateUrl: './company-entity-relationship-table.component.html',
	styleUrls: [
		'./company-entity-relationship-table.component.css',
		'../../ui/common/main.scss',
	],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetRowClickDirective,
		AddressPipe,
		DataSetLoadingRowComponent,
	],
})
export class CompanyEntityRelationshipTableComponent
	implements AfterViewInit, OnDestroy, OnInit
{
	@Input() dataRequest!: (
		queryParams: QueryParam[]
	) => Promise<CompanyEntityRelationship[]>;

	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	displayedColumns: string[] = [
		'companyName',
		'relationship',
		'address',
		'active',
	];
	dataSource = new MatTableDataSource<CompanyEntityRelationship>();
	loadingTable = false;
	isOracle = false;

	private filterSubscription?: Subscription;

	filterLabels = {
		defaultPlaceholder: $localize`:@@companyEntityRelationshipTable-defaultPlaceholder:Search company name...`,
		companyName: $localize`:@@companyEntityRelationshipTable-companyNameLabel:Company Name`,
		status: $localize`:@@companyEntityRelationshipTable-statusLabel:Status`,
		relationship: $localize`:@@companyEntityRelationshipTable-relationshipLabel:Relationship`,
		streetAddress: $localize`:@@companyEntityRelationshipTable-streetAddressLabel:Street Address`,
		townCity: $localize`:@@companyEntityRelationshipTable-townCityLabel:Town / City`,
		stateProvince: $localize`:@@companyEntityRelationshipTable-stateProvinceLabel:State / Province`,
		postalCode: $localize`:@@companyEntityRelationshipTable-postalCodeLabel:Postal Code`,
		country: $localize`:@@companyEntityRelationshipTable-countryLabel:Country`,
	};

	relationshipOptions: DropdownOption[] = [
		{
			value: 'client',
			label: $localize`:@@companyEntityRelationshipTable-relationshipFilterOptionClient:Client`,
		},
		{
			value: 'contractor',
			label: $localize`:@@companyEntityRelationshipTable-relationshipFilterOptionContractor:Contractor`,
		},
	];

	activeOptions: DropdownOption[] = [
		{
			value: '1',
			label: $localize`:@@companyEntityRelationshipTable-activeFilterOptionActive:Active`,
		},
		{
			value: '0',
			label: $localize`:@@companyEntityRelationshipTable-activeFilterOptionInactive:Inactive`,
		},
	];

	companyLoadingKey = ApiLoadingKeys.getCompanyRelationships;
	finishedLoadingInitData = false;

	constructor(
		private dialog: MatDialog,
		private router: Router,
		public readonly loadingService: LoadingIndicatorService,
		private readonly scService: SystemCapabilityService
	) {}

	ngOnInit(): void {
		this.isOracle = this.scService.isOracle;
		if (this.isOracle) {
			this.displayedColumns.push('slugId');
		}
	}

	ngAfterViewInit(): void {
		this.filterSubscription = this.dataSet.queryParams
			.pipe(switchMap((filter) => this.dataRequest(filter)))
			.subscribe((data) => {
				this.finishedLoadingInitData = true;
				this.dataSource.data = data;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	async onRowClick(
		companyEntityRelationship: CompanyEntityRelationship
	): Promise<void> {
		this.router.navigate([
			'/company',
			companyEntityRelationship.company.id,
			'detail',
		]);
	}

	rowId(row: CompanyEntityRelationship): string {
		return row?.entityRelationship?.id || 'unknown-id';
	}
}
